import {
  captureCueboardObservabilityEvent,
  sanitizeTelemetryRoute,
} from '@/utils/posthog';

import { HTTPStatusError } from './chat-api';

export type TelegramRuntimeStatus = {
  platform: string;
  configured: boolean;
  online: boolean;
  config_error?: string;
  recent_activity_at?: number | null;
  recent_error?: string;
};

export type TelegramDMLink = {
  telegram_user_id: string;
  telegram_username?: string;
  created_at: string;
  updated_at: string;
};

export type TelegramDMClaimCode = {
  code: string;
  expires_at: string;
};

export type TelegramDMState = {
  runtime?: TelegramRuntimeStatus | null;
  dm_link?: TelegramDMLink | null;
  dm_link_code?: TelegramDMClaimCode | null;
  official_login_enabled?: boolean;
};

export type TelegramConnectStartResponse = {
  authorization_url: string;
};

export type TelegramConnectCompleteResponse = {
  message?: string;
  telegram_user_id?: string;
  telegram_username?: string;
  redirect_url?: string;
};

type RequestInitWithBody = RequestInit & {
  body?: BodyInit | null;
};

function buildErrorMessage(status: number, body: unknown): string {
  if (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof body.error === 'object' &&
    body.error !== null &&
    'message' in body.error &&
    typeof body.error.message === 'string'
  ) {
    return body.error.message;
  }

  if (typeof body === 'string' && body.trim()) {
    return body.trim();
  }

  return `Request failed (${status})`;
}

async function readErrorBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return response.json().catch(() => null);
  }
  return response.text().catch(() => null);
}

async function fetchTelegramJSON<T>(
  path: string,
  init?: RequestInitWithBody
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      credentials: 'include',
      ...init,
    });
  } catch (error) {
    captureTelegramFailure(path, init?.method, undefined, error);
    throw error;
  }

  if (!response.ok) {
    const body = await readErrorBody(response);
    const message = buildErrorMessage(response.status, body);
    if (response.status >= 500) {
      captureTelegramFailure(
        path,
        init?.method,
        response.status,
        new Error(message)
      );
    }
    throw new HTTPStatusError(response.status, message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

function captureTelegramFailure(
  path: string,
  method: string | undefined,
  statusCode: number | undefined,
  error: unknown
) {
  const routeClass = sanitizeTelemetryRoute(path);
  captureCueboardObservabilityEvent({
    failureClass: 'integration.telegram_failed',
    severity: 'error',
    surface: 'website_integration',
    platform: 'web',
    entrySurface: 'cueboard_web_app',
    integrationProvider: 'telegram',
    integrationSurface: routeClass.includes('/connect/') ? 'oauth' : 'dm',
    statusCode,
    networkErrorKind: statusCode == null ? 'transport_error' : undefined,
    diagnosisHint:
      'Use request_id if present and the matching backend route_class to inspect Telegram integration logs.',
    error,
    properties: {
      method: method || 'GET',
      route_class: routeClass,
      suspect_layer: statusCode == null ? 'client_network' : 'integration',
    },
  });
}

export async function getTelegramDMState(): Promise<TelegramDMState> {
  return fetchTelegramJSON<TelegramDMState>('/v1/user/telegram', {
    method: 'GET',
  });
}

export async function createTelegramDMClaimCode(): Promise<TelegramDMClaimCode> {
  return fetchTelegramJSON<TelegramDMClaimCode>(
    '/v1/user/telegram/dm-link-code',
    {
      method: 'POST',
    }
  );
}

export async function deleteTelegramDMLink(): Promise<void> {
  await fetchTelegramJSON<void>('/v1/user/telegram/dm-link', {
    method: 'DELETE',
  });
}

export async function startTelegramConnect(): Promise<TelegramConnectStartResponse> {
  return fetchTelegramJSON<TelegramConnectStartResponse>(
    '/v1/user/telegram/connect/start',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    }
  );
}

export async function completeTelegramConnect(
  code: string,
  state: string
): Promise<TelegramConnectCompleteResponse> {
  return fetchTelegramJSON<TelegramConnectCompleteResponse>(
    '/v1/integrations/telegram/connect/complete',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code, state }),
    }
  );
}
